import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import CardOperationsModal from "../../elements/dashboard/CardOperationsModal";
import MainBalanceCard from "../../elements/dashboard/MainBalanceCard";
import CardAccessNotice from "../../components/CardAccessNotice";
import { AuthContext } from "../../../context/authContext";
import { request } from "../../../utils/api";
import useKycApprovalStatus from "../../hooks/useKycApprovalStatus";
import { buildCardKycFlowState } from "../../hooks/buildCardKycFlowState";
import {
  getSecurityCodeStatus,
  validateSecurityCode,
} from "../../../services/securityCode";
import { formatMoney } from "../../../utils";

const extractCardsRows = (response) => {
  const payload = response?.data?.data ?? response?.data ?? [];

  if (Array.isArray(payload)) {
    return { rows: payload, lastPage: 1 };
  }

  if (payload && typeof payload === "object" && Array.isArray(payload?.data)) {
    const parsedLastPage = Number(payload?.last_page || 1);
    return {
      rows: payload.data,
      lastPage:
        Number.isFinite(parsedLastPage) && parsedLastPage > 0
          ? parsedLastPage
          : 1,
    };
  }

  return { rows: [], lastPage: 1 };
};

const normalizeLabel = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "N/A";
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizeCardType = (value) => {
  if (value === 1 || value === "1") return "Physical";
  if (value === 2 || value === "2") return "Virtual";
  const text = String(value || "").toLowerCase();
  if (text.includes("virtual")) return "Virtual";
  if (text.includes("physical")) return "Physical";
  return normalizeLabel(value);
};

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const Cards = () => {
  const { user } = useContext(AuthContext);
  const {
    loading: kycLoading,
    error: kycError,
    isApproved: isKycApproved,
    hasSubmittedKyc,
    statusLabel: kycStatusLabel,
  } = useKycApprovalStatus();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [walletSummary, setWalletSummary] = useState({
    balance: null,
    availableBalance: null,
    currency: "USD",
    assetName: "",
    status: "",
  });
  const [walletLoading, setWalletLoading] = useState(false);
  const [tableFiltersOpen, setTableFiltersOpen] = useState(false);
  const [tableFilters, setTableFilters] = useState({
    type: "all",
    status: "all",
    bound: "all",
  });
  const [overviewModal, setOverviewModal] = useState({
    open: false,
    title: "",
    subtitle: "",
    rows: [],
  });
  const [showCardOpsModal, setShowCardOpsModal] = useState(false);
  const [cardOpsScreen, setCardOpsScreen] = useState("all");
  const [securityStatusLoading, setSecurityStatusLoading] = useState(true);
  const [hasSecurityCode, setHasSecurityCode] = useState(false);
  const [balanceAccessUnlocked, setBalanceAccessUnlocked] = useState(false);
  const [securityModalOpen, setSecurityModalOpen] = useState(false);
  const [securityCodeInput, setSecurityCodeInput] = useState("");
  const [securityCodeSubmitting, setSecurityCodeSubmitting] = useState(false);
  const [securityCodeError, setSecurityCodeError] = useState("");

  const userId = user?.id;
  const userCode = user?.tevau_user?.user_code || null;
  const thirdId = user?.tevau_user?.third_id || null;

  const loadCards = useCallback(
    async ({ silent = false } = {}) => {
      if (!userId) {
        setCards([]);
        setError("");
        return [];
      }

      if (!silent) {
        setLoading(true);
      }
      setError("");

      try {
        const firstResponse = await request({
          url: "app/tevau/cards",
          method: "GET",
        });

        const firstPage = extractCardsRows(firstResponse);
        let rows = firstPage.rows;

        if (firstPage.lastPage > 1) {
          const pageRequests = Array.from(
            { length: firstPage.lastPage - 1 },
            (_, index) =>
              request({
                url: `app/tevau/cards?page=${index + 2}`,
                method: "GET",
              }),
          );
          const pageResponses = await Promise.all(pageRequests);
          const extraRows = pageResponses.flatMap(
            (pageRes) => extractCardsRows(pageRes).rows,
          );
          rows = [...rows, ...extraRows];
        }

        const deduped = Array.from(
          new Map(
            rows.map((row, index) => [
              String(row?.id ?? row?.card_id ?? `row-${index}`),
              row,
            ]),
          ).values(),
        );

        const filtered = deduped.filter((row) => {
          const rowUserCode = row?.user_code;
          const rowThirdId = row?.third_id;
          const rowUserId = row?.card_account?.user_id;
          return (
            (userCode && rowUserCode === userCode) ||
            (thirdId && rowThirdId === thirdId) ||
            (userId && Number(rowUserId) === Number(userId))
          );
        });

        setCards(filtered);
        return filtered;
      } catch (fetchError) {
        setCards([]);
        setError("Failed to load cards");
        return [];
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [thirdId, userCode, userId],
  );

  const loadWalletBalance = useCallback(async () => {
    if (!userId && !userCode && !thirdId) {
      setWalletSummary({
        balance: null,
        availableBalance: null,
        currency: "USD",
        assetName: "",
        status: "",
      });
      return null;
    }

    setWalletLoading(true);
    try {
      const res = await request({
        url: "wallet/balance",
        method: "GET",
        data: {
          ...(userCode ? { user_code: userCode } : {}),
          ...(thirdId ? { third_id: thirdId } : {}),
          ...(userId ? { user_id: userId } : {}),
        },
      });

      const payload = res?.data ?? {};
      const assets = Array.isArray(payload?.assets) ? payload.assets : [];
      const bestAsset =
        [...assets].sort((a, b) => {
          const aValue = Number(a?.available_balance ?? a?.balance ?? 0) || 0;
          const bValue = Number(b?.available_balance ?? b?.balance ?? 0) || 0;
          return bValue - aValue;
        })[0] || null;

      setWalletSummary({
        balance: bestAsset?.balance ?? null,
        availableBalance: bestAsset?.available_balance ?? bestAsset?.balance ?? null,
        currency: bestAsset?.currency || "USD",
        assetName: bestAsset?.name || "",
        status: bestAsset?.status || "",
      });

      return bestAsset;
    } catch (walletError) {
      setWalletSummary((prev) => ({
        ...prev,
        balance: null,
        availableBalance: null,
      }));
      return null;
    } finally {
      setWalletLoading(false);
    }
  }, [thirdId, userCode, userId]);

  useEffect(() => {
    loadCards().catch(() => undefined);
    loadWalletBalance().catch(() => undefined);
  }, [loadCards, loadWalletBalance]);

  const loadSecurityStatus = useCallback(async () => {
    setSecurityStatusLoading(true);
    try {
      const result = await getSecurityCodeStatus();
      setHasSecurityCode(Boolean(result?.hasSecurityCode));
    } catch (error) {
      setHasSecurityCode(true);
    } finally {
      setSecurityStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSecurityStatus().catch(() => undefined);
  }, [loadSecurityStatus]);

  const openBalanceUnlockModal = () => {
    if (securityStatusLoading) return;
    setSecurityCodeError(
      hasSecurityCode
        ? ""
        : "Security code is not configured. Set it from Profile settings first.",
    );
    setSecurityCodeInput("");
    setSecurityModalOpen(true);
  };

  const verifyBalanceAccess = async () => {
    if (!hasSecurityCode) {
      setSecurityCodeError(
        "Security code is not configured. Set it from Profile settings first.",
      );
      return;
    }
    if (!String(securityCodeInput || "").trim()) {
      setSecurityCodeError("Please enter security code.");
      return;
    }

    setSecurityCodeSubmitting(true);
    setSecurityCodeError("");
    try {
      await validateSecurityCode({ securityCode: securityCodeInput });
      setBalanceAccessUnlocked(true);
      setSecurityModalOpen(false);
      setSecurityCodeInput("");
    } catch (error) {
      setSecurityCodeError(
        error?.response?.data?.message || "Security code validation failed.",
      );
    } finally {
      setSecurityCodeSubmitting(false);
    }
  };

  const toggleBalanceVisibility = () => {
    if (balanceAccessUnlocked) {
      setBalanceAccessUnlocked(false);
      return;
    }
    openBalanceUnlockModal();
  };

  const formatProtectedMoney = useCallback(
    (value, currency = "USD") =>
      balanceAccessUnlocked ? formatMoney(value, currency) : "****",
    [balanceAccessUnlocked],
  );

  const stats = useMemo(() => {
    const activeCount = cards.filter((card) =>
      ["active", "normal"].includes(String(card?.status || "").toLowerCase()),
    ).length;
    const virtualCount = cards.filter(
      (card) => normalizeCardType(card?.card_type || card?.type) === "Virtual",
    ).length;
    const physicalCount = cards.filter(
      (card) => normalizeCardType(card?.card_type || card?.type) === "Physical",
    ).length;
    const totalBalance = cards.reduce((sum, card) => {
      const value = Number(card?.balance || 0);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
    const balanceCurrency = cards.find((card) => card?.currency)?.currency || "USD";

    return {
      total: cards.length,
      active: activeCount,
      virtual: virtualCount,
      physical: physicalCount,
      totalBalance,
      balanceCurrency,
    };
  }, [cards]);

  const overviewCards = useMemo(
    () => [
      {
        key: "total",
        title: "Total Cards",
        value: stats.total,
        sub: `${stats.active} active`,
        tone: "is-blue",
      },
      {
        key: "active",
        title: "Active Cards",
        value: stats.active,
        sub: "Active / Normal",
        tone: "is-slate",
      },
      {
        key: "virtual",
        title: "Virtual Cards",
        value: stats.virtual,
        sub: "Instant issue",
        tone: "is-purple",
      },
      {
        key: "physical",
        title: "Physical Cards",
        value: stats.physical,
        sub: "Courier flow",
        tone: "is-orange",
      },
      {
        key: "balance",
        title: "Total Balance",
        value: formatProtectedMoney(stats.totalBalance, stats.balanceCurrency),
        sub: `${String(stats.balanceCurrency || "USD").toUpperCase()} cards`,
        tone: "is-green",
      },
      {
        key: "wallet",
        title: "Wallet Available",
        value: walletLoading
          ? "Refreshing..."
          : formatProtectedMoney(
              walletSummary.availableBalance ?? walletSummary.balance ?? 0,
              walletSummary.currency || "USD",
            ),
        sub: walletSummary.assetName || "Wallet auto refresh",
        tone: "is-cyan",
      },
      {
        key: "payment",
        title: "Payment Mode",
        value: "Wallet Auto Debit",
        sub: "After order submit",
        tone: "is-rose",
      },
    ],
    [formatProtectedMoney, stats, walletLoading, walletSummary],
  );

  const getOverviewFilteredRows = useCallback(
    (key) => {
      if (!Array.isArray(cards)) return [];

      if (key === "virtual") {
        return cards.filter(
          (card) =>
            normalizeCardType(card?.card_type || card?.type).toLowerCase() === "virtual",
        );
      }

      if (key === "physical") {
        return cards.filter(
          (card) =>
            normalizeCardType(card?.card_type || card?.type).toLowerCase() === "physical",
        );
      }

      if (key === "active") {
        return cards.filter((card) =>
          ["active", "normal"].includes(String(card?.status || "").toLowerCase()),
        );
      }

      if (key === "balance") {
        return cards.filter((card) => Number(card?.balance || 0) > 0);
      }

      if (key === "wallet") {
        const walletCode = String(walletSummary.currency || "").toUpperCase();
        return cards.filter(
          (card) => String(card?.currency || "").toUpperCase() === walletCode,
        );
      }

      return cards;
    },
    [cards, walletSummary.currency],
  );

  const openOverviewFilteredModal = useCallback(
    (item) => {
      const rows = getOverviewFilteredRows(item.key);
      setOverviewModal({
        open: true,
        title: item.title,
        subtitle: `${rows.length} card(s) matched`,
        rows,
      });
    },
    [getOverviewFilteredRows],
  );

  const filteredCards = useMemo(() => {
    return cards.filter((card) => {
      const cardType = normalizeCardType(card?.card_type || card?.type).toLowerCase();
      const cardStatus = String(card?.status || "").trim().toLowerCase();
      const boundValue =
        typeof card?.is_bound === "boolean"
          ? card.is_bound
            ? "yes"
            : "no"
          : "na";

      const typePass =
        tableFilters.type === "all" || cardType === tableFilters.type;
      const statusPass =
        tableFilters.status === "all" || cardStatus === tableFilters.status;
      const boundPass =
        tableFilters.bound === "all" || boundValue === tableFilters.bound;

      return typePass && statusPass && boundPass;
    });
  }, [cards, tableFilters]);

  const tableFilterOptions = useMemo(() => {
    const statuses = Array.from(
      new Set(
        cards
          .map((card) => String(card?.status || "").trim().toLowerCase())
          .filter(Boolean),
      ),
    );
    return {
      statuses,
    };
  }, [cards]);
  const hasPurchasedCard = cards.length > 0;
  const cardFlow = useMemo(
    () =>
      buildCardKycFlowState({
        hasSubmittedKyc,
        isKycApproved,
        kycStatusLabel,
        hasPurchasedCard,
      }),
    [hasPurchasedCard, hasSubmittedKyc, isKycApproved, kycStatusLabel],
  );

  return (
    <>
      <PageTitle motherMenu="Home" motherMenuPath="/" activeMenu="Cards" />

      <div className="row g-3">
        {!loading && !hasPurchasedCard && !cardFlow.canOrderCard ? (
          <div className="col-12">
            <CardAccessNotice
              title={cardFlow.title}
              message={cardFlow.message}
            />
          </div>
        ) : null}

        {hasPurchasedCard ? (
          <>
        <div className="col-12">
          <div className="card nova-panel h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0">Cards Overview</h5>
              </div>
              {!hasSecurityCode && !securityStatusLoading ? (
                <div className="alert alert-warning py-2">
                  Security code is not configured. Setup from Profile tab first.
                </div>
              ) : null}

              <div className="nova-cards-overview-board nova-cards-overview-horizontal">
                <div className="nova-overview-stage-card nova-overview-stage-strip">
                  <div className="nova-overview-stage-main">
                    <div className="nova-overview-stage-head">
                      <span className="nova-overview-stage-title">Payment Summary</span>
                    </div>
                    <div className="nova-overview-stage-stats">
                      <strong>{formatProtectedMoney(stats.totalBalance, stats.balanceCurrency)}</strong>
                      <button
                        type="button"
                        className={`nova-sec-visibility-toggle is-compact ${
                          balanceAccessUnlocked ? "is-active" : ""
                        }`}
                        onClick={toggleBalanceVisibility}
                        disabled={securityStatusLoading}
                        aria-label={
                          balanceAccessUnlocked
                            ? "Hide balances"
                            : "Show balances"
                        }
                        title={
                          balanceAccessUnlocked
                            ? "Hide balances"
                            : "Show balances"
                        }
                      >
                        <i
                          className={`pi ${
                            balanceAccessUnlocked ? "pi-eye-slash" : "pi-eye"
                          }`}
                        />
                      </button>
                      <span />
                      <small>{stats.total} Cards</small>
                    </div>
                  </div>
                  <div className="nova-overview-stage-cta">
                    <i className="pi pi-wallet" />
                    Deposits, cards & wallet activity
                  </div>
                </div>

                <div className="nova-overview-horizontal-scroll">
                  <div className="nova-overview-metric-grid nova-overview-metric-grid-horizontal">
                  {overviewCards.map((item) => (
                    <div
                      key={item.key}
                      className={`nova-overview-metric-card ${item.tone}`}
                    >
                      <div className="nova-overview-metric-icon">
                        <span />
                      </div>
                      <div className="nova-overview-metric-content">
                        <h6>{item.title}</h6>
                        <strong>{item.value}</strong>
                        <p>{item.sub}</p>
                      </div>
                      {/* <button
                        type="button"
                        className="nova-overview-metric-arrow"
                        aria-label={`${item.title} details`}
                        onClick={() => openOverviewFilteredModal(item)}
                      >
                        <i className="pi pi-angle-right" />
                      </button> */}
                    </div>
                  ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card nova-panel nova-card-ops-launch">
            <div className="card-body d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
              <div>
                <div className="nova-flow-kicker mb-1">Cards Management</div>
                <h4 className="mb-1">Bind Your Card</h4>
                <p className="mb-0 text-muted">
                  Link your physical card by entering the activation code, card number, and address details.
                </p>
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    if (!cardFlow.canBindCard) return;
                    setCardOpsScreen("bind");
                    setShowCardOpsModal(true);
                  }}
                  disabled={!cardFlow.canBindCard}
                >
                  <i className="pi pi-link me-2" />Open Card Bind
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <MainBalanceCard
            cards={cards}
            userName={user?.name || user?.full_name || "User"}
            loading={loading}
            onCardsUpdated={() => loadCards({ silent: true })}
            onWalletUpdated={loadWalletBalance}
            walletAsset={{
              balance: walletSummary.balance,
              available_balance: walletSummary.availableBalance,
              currency: walletSummary.currency,
              name: walletSummary.assetName,
              status: walletSummary.status,
            }}
          />
        </div>

        <div className="col-12">
          <div className="card nova-panel h-100">
            <div className="card-body">
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-3">
                <h5 className="mb-0">User Cards</h5>
                <div className="nova-table-tools">
                  <span className="text-muted small">
                    {loading ? "Loading..." : `${filteredCards.length} / ${cards.length} records`}
                  </span>
                  <div className="nova-table-filter-wrap">
                    <button
                      type="button"
                      className="nova-overview-metric-arrow"
                      aria-label="Open table filters"
                      onClick={() => setTableFiltersOpen((prev) => !prev)}
                    >
                      <i className="pi pi-filter" />
                    </button>
                    {tableFiltersOpen && (
                      <div className="nova-table-filter-popup">
                        <div className="nova-table-filter-title">Filter Cards</div>

                        <label className="nova-table-filter-field">
                          <span>Type</span>
                          <select
                            className="form-select form-select-sm"
                            value={tableFilters.type}
                            onChange={(event) =>
                              setTableFilters((prev) => ({
                                ...prev,
                                type: event.target.value,
                              }))
                            }
                          >
                            <option value="all">All</option>
                            <option value="physical">Physical</option>
                            <option value="virtual">Virtual</option>
                          </select>
                        </label>

                        <label className="nova-table-filter-field">
                          <span>Status</span>
                          <select
                            className="form-select form-select-sm"
                            value={tableFilters.status}
                            onChange={(event) =>
                              setTableFilters((prev) => ({
                                ...prev,
                                status: event.target.value,
                              }))
                            }
                          >
                            <option value="all">All</option>
                            {tableFilterOptions.statuses.map((status) => (
                              <option key={status} value={status}>
                                {normalizeLabel(status)}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="nova-table-filter-field">
                          <span>Bound</span>
                          <select
                            className="form-select form-select-sm"
                            value={tableFilters.bound}
                            onChange={(event) =>
                              setTableFilters((prev) => ({
                                ...prev,
                                bound: event.target.value,
                              }))
                            }
                          >
                            <option value="all">All</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                            <option value="na">N/A</option>
                          </select>
                        </label>

                        <div className="nova-table-filter-actions">
                          <button
                            type="button"
                            className="btn btn-sm btn-light"
                            onClick={() => {
                              setTableFilters({
                                type: "all",
                                status: "all",
                                bound: "all",
                              });
                            }}
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => setTableFiltersOpen(false)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {error ? <div className="alert alert-warning py-2">{error}</div> : null}

              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Card ID</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Balance</th>
                      <th>Bound</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && filteredCards.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          {cards.length
                            ? "No cards matched current filters."
                            : "No cards found for this user."}
                        </td>
                      </tr>
                    ) : (
                      filteredCards.map((card) => (
                        <tr key={String(card?.id ?? card?.card_id)}>
                          <td>{card?.card_id || card?.id || "N/A"}</td>
                          <td>{normalizeCardType(card?.card_type || card?.type)}</td>
                          <td>{normalizeLabel(card?.status)}</td>
                          <td>{formatProtectedMoney(card?.balance, card?.currency || "USD")}</td>
                          <td>
                            {typeof card?.is_bound === "boolean"
                              ? card.is_bound
                                ? "Yes"
                                : "No"
                              : "N/A"}
                          </td>
                          <td>{formatDateTime(card?.created_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
          </>
        ) : (
          <div className="col-12">
            <div className="nova-first-card-hero">
              {/* Left — icon + heading */}
              <div className="nova-first-card-left">
                <div className="nova-first-card-icon">
                  <i className="pi pi-credit-card" />
                </div>
                <div className="nova-flow-kicker mb-2">Getting Started</div>
                <h3 className="nova-first-card-title">Buy Your First Card</h3>
                <p className="nova-first-card-desc">{cardFlow.message}</p>

                <div className="nova-first-card-chips">
                  <span className={`nova-first-card-chip ${isKycApproved ? "is-done" : "is-pending"}`}>
                    <i className={`pi ${isKycApproved ? "pi-check-circle" : "pi-clock"}`} />
                    KYC: {kycLoading ? "Checking..." : kycStatusLabel}
                  </span>
                  <span className={`nova-first-card-chip ${hasSecurityCode ? "is-done" : "is-pending"}`}>
                    <i className={`pi ${hasSecurityCode ? "pi-check-circle" : "pi-shield"}`} />
                    Security Code: {securityStatusLoading ? "Checking..." : hasSecurityCode ? "Set" : "Not Set"}
                  </span>
                </div>

                {kycError ? (
                  <div className="nova-kyc-feedback is-error mt-3">
                    <i className="fa fa-exclamation-circle" />
                    <span>{kycError}</span>
                  </div>
                ) : null}
              </div>

              {/* Right — steps + CTA */}
              <div className="nova-first-card-right">
                <div className="nova-first-card-steps">
                  <div className={`nova-first-card-step ${isKycApproved ? "is-done" : "is-active"}`}>
                    <div className="nova-first-card-step-num">
                      {isKycApproved ? <i className="pi pi-check" /> : "1"}
                    </div>
                    <div>
                      <strong>Complete KYC</strong>
                      <p>Submit your identity verification</p>
                    </div>
                    {isKycApproved && <i className="pi pi-check-circle nova-first-card-step-tick" />}
                  </div>
                  <div className={`nova-first-card-step ${hasSecurityCode ? "is-done" : isKycApproved ? "is-active" : ""}`}>
                    <div className="nova-first-card-step-num">
                      {hasSecurityCode ? <i className="pi pi-check" /> : "2"}
                    </div>
                    <div>
                      <strong>Set Security Code</strong>
                      <p>Configure from Profile Settings</p>
                    </div>
                    {hasSecurityCode && <i className="pi pi-check-circle nova-first-card-step-tick" />}
                  </div>
                  <div className={`nova-first-card-step ${cardFlow.canOrderCard ? "is-active" : ""}`}>
                    <div className="nova-first-card-step-num">3</div>
                    <div>
                      <strong>Order Your Card</strong>
                      <p>Use Order Card from the menu when KYC is complete</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal
        show={overviewModal.open}
        onHide={() =>
          setOverviewModal({ open: false, title: "", subtitle: "", rows: [] })
        }
        centered
        size="xl"
      >
        <div className="modal-header">
          <div>
            <h5 className="modal-title">{overviewModal.title || "Filtered Cards"}</h5>
            <div className="text-muted small">
              {overviewModal.subtitle || "Cards list"}
            </div>
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={() =>
              setOverviewModal({ open: false, title: "", subtitle: "", rows: [] })
            }
            aria-label="Close"
          />
        </div>
        <div className="modal-body">
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th>Card ID</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Balance</th>
                  <th>Bound</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {overviewModal.rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      No cards found for this filter.
                    </td>
                  </tr>
                ) : (
                  overviewModal.rows.map((card) => (
                    <tr key={`overview-${String(card?.id ?? card?.card_id)}`}>
                      <td>{card?.card_id || card?.id || "N/A"}</td>
                      <td>{normalizeCardType(card?.card_type || card?.type)}</td>
                      <td>{normalizeLabel(card?.status)}</td>
                      <td>{formatProtectedMoney(card?.balance, card?.currency || "USD")}</td>
                      <td>
                        {typeof card?.is_bound === "boolean"
                          ? card.is_bound
                            ? "Yes"
                            : "No"
                          : "N/A"}
                      </td>
                      <td>{formatDateTime(card?.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>

       <CardOperationsModal
        show={showCardOpsModal}
        onHide={() => {
          setShowCardOpsModal(false);
          setCardOpsScreen("all");
        }}
        screen={hasPurchasedCard ? cardOpsScreen : "order"}
        user={user}
        userCards={cards}
        walletSummary={walletSummary}
        canOrderCards={cardFlow.canOrderCard}
        canBindCards={cardFlow.canBindCard}
        orderDisabledReason={cardFlow.orderBlockedReason}
        bindDisabledReason={cardFlow.bindBlockedReason}
        onCardsUpdated={() => loadCards({ silent: true })}
        onWalletUpdated={loadWalletBalance}
      />

      <Modal show={securityModalOpen} onHide={() => setSecurityModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Security Code Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            type="password"
            className="form-control"
            value={securityCodeInput}
            onChange={(event) => setSecurityCodeInput(event.target.value)}
            placeholder="Security code"
          />
          {securityCodeError ? (
            <div className="alert alert-danger mt-3 mb-0 py-2">
              {securityCodeError}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => setSecurityModalOpen(false)}
            disabled={securityCodeSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={verifyBalanceAccess}
            disabled={securityCodeSubmitting}
          >
            {securityCodeSubmitting ? "Verifying..." : "Verify"}
          </button>
        </Modal.Footer>
      </Modal>

    </>
  );
};

export default Cards;
